import React from 'react';
import { Sparkles, Trash2, Sun, Moon, X, Loader2 } from 'lucide-react';
import { LiveKitRoom, RoomAudioRenderer } from '@livekit/components-react';
import ChatInterface, { Message } from '../ChatInterface';
import { VoiceAgent } from '../VoiceAgent';
import WelcomeScreen from '../WelcomeScreen';
import { Theme, createTheme } from '../../types/theme';
import { ActiveView } from '../../types';
import { AppConfig } from '../../types/types';
import { ConnectionDetails } from '../../hooks/useConnectionDetails';

interface WidgetViewProps {
    theme: Theme;
    onThemeChange: (theme: Theme) => void;
    hasSession: boolean;
    clearSession: () => void;
    activeView: ActiveView;
    onViewChange: (view: ActiveView) => void;
    agentName?: string;
    messages: Message[];
    addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => string;
    updateMessage: (id: string, updates: Partial<Message>) => void;
    userName: string | null;
    sessionId: string;
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
    connectionDetails: ConnectionDetails | null;
    appConfig: AppConfig;
    isVoiceSessionActive: boolean;
    isVoiceConnecting: boolean;
    isPreparingToken: boolean;
    handleDisconnect: () => void;
    onClose: () => void;
    preflightVoiceCheck: () => Promise<boolean>;
    isInitialized: boolean;
    onWelcomeComplete: (name: string) => void;
    updateIsProfileUpdated: (isProfileUpdated: boolean) => void;
    companyName?: string;
}

const WidgetView: React.FC<WidgetViewProps> = ({
    theme,
    onThemeChange,
    hasSession,
    clearSession,
    activeView,
    onViewChange,
    agentName,
    messages,
    addMessage,
    updateMessage,
    userName,
    sessionId,
    setMessages,
    connectionDetails,
    appConfig,
    isVoiceSessionActive,
    isVoiceConnecting,
    isPreparingToken,
    handleDisconnect,
    onClose,
    preflightVoiceCheck,
    isInitialized,
    onWelcomeComplete,
    updateIsProfileUpdated,
    companyName
}) => {
    const handleThemeToggle = () => {
        const newMode = theme.mode === 'dark' ? 'light' : 'dark';
        onThemeChange(createTheme(newMode, theme.primaryColor));
    };

    const renderContent = () => {
        if (!isInitialized) {
            return (
                <div className="flex items-center justify-center min-h-full py-8">
                    <div className="text-center">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: theme.primaryColor }} />
                    </div>
                </div>
            );
        }

        if (!hasSession) {
            return <WelcomeScreen theme={theme} agentName={agentName} onStart={onWelcomeComplete} />;
        }

        if (activeView === 'chat') {
            return (
                <ChatInterface
                    viewMode="widget"
                    onModeChange={() => { }}
                    onViewChange={onViewChange}
                    theme={theme}
                    agentName={agentName}
                    onThemeChange={onThemeChange}
                    onClearSession={clearSession}
                    messages={messages}
                    addMessage={addMessage}
                    updateMessage={updateMessage}
                    userName={userName}
                    sessionId={sessionId}
                    setMessages={setMessages}
                    preflightVoiceCheck={preflightVoiceCheck}
                    updateIsProfileUpdated={updateIsProfileUpdated}
                    companyName={companyName}
                />
            );
        }

        if (activeView === 'voice' && connectionDetails && isVoiceSessionActive) {
            if (isVoiceConnecting || !connectionDetails) {
                return (
                    <div className="flex items-center justify-center min-h-full py-8">
                        <div className="text-center">
                            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" style={{ color: theme.primaryColor }} />
                            <div style={{ color: theme.textSecondaryColor }}>
                                {!connectionDetails ? 'Preparing voice connection...' : 'Connecting to voice agent...'}
                            </div>
                        </div>
                    </div>
                );
            }

            return (
                <LiveKitRoom
                    token={connectionDetails.token}
                    serverUrl={appConfig.liveKitUrl}
                    connect={isVoiceSessionActive}
                    audio={true}
                    video={false}
                    onDisconnected={handleDisconnect}
                    className="h-full pointer-events-none"
                >
                    <div className="pointer-events-auto h-full">
                        <VoiceAgent
                            appConfig={appConfig}
                            disabled={!isVoiceSessionActive}
                            sessionStarted={isVoiceSessionActive}
                            isWidget={true}
                            onDisconnect={handleDisconnect}
                            theme={theme}
                        />
                    </div>
                    <RoomAudioRenderer />
                </LiveKitRoom>
            );
        }
        return null;
    };

    return (
        <div className="fixed bottom-4 right-4 w-full sm:w-[30rem] max-w-[calc(100vw-2rem)] widget-container pointer-events-auto">
            <div
                className="relative w-full h-full border rounded-3xl shadow-lg overflow-hidden transition-colors duration-300 flex flex-col"
                style={{
                    backgroundColor: theme.backgroundColor,
                    borderColor: theme.borderColor,
                    boxShadow: `0 4px 6px -1px ${theme.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                }}
            >
                <div
                    className="relative z-[9999] flex items-center justify-between px-3 py-2 border-b transition-all duration-300"
                    style={{
                        borderColor: theme.borderColor,
                        backgroundColor: theme.surfaceColor
                    }}
                >
                    <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                            <Sparkles
                                size={16}
                                className="animate-pulse"
                                style={{ color: theme.primaryColor }}
                            />

                            <span
                                className="text-sm font-medium flex items-center space-x-2"
                                style={{ color: theme.textColor }}
                            >
                                Smart Agent
                            </span>
                        </div>
                        {isPreparingToken && (
                            <Loader2 className="w-3 h-3 animate-spin" style={{ color: theme.primaryColor }} />
                        )}
                    </div>
                    <div className="flex items-center space-x-1">
                        {hasSession && (
                            <button
                                onClick={clearSession}
                                className="p-1.5 rounded-lg transition-all duration-200 hover:opacity-80"
                                style={{
                                    color: theme.textSecondaryColor,
                                    backgroundColor: `${theme.surfaceColor}80`
                                }}
                                title="Clear session"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        )}

                        <button
                            onClick={handleThemeToggle}
                            className="p-1.5 rounded-lg transition-all duration-200 hover:opacity-80"
                            style={{
                                color: theme.textSecondaryColor,
                                backgroundColor: `${theme.surfaceColor}80`
                            }}
                            title="Toggle theme"
                        >
                            {theme.mode === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
                        </button>

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onClose?.();
                            }}
                            className="p-1.5 rounded-lg transition-colors hover:opacity-80"
                            style={{
                                color: theme.textSecondaryColor,
                                backgroundColor: `${theme.surfaceColor}80`
                            }}
                            title="Close"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
                <div className="flex-1 min-h-0">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default WidgetView;
